import type { FC, ReactNode } from 'react';
import { useCallback, useEffect, useEffectEvent, useMemo, useRef, useState } from 'react';

import { useLocation, useSearchParams } from 'react-router';

import { SHOW_SELECTED_URL_PARAM } from '@/constants/constants';
import { useAppSelector } from '@/hooks/redux-hooks';
import { extractFiltersFromParams } from '@/pages/Catalog/utils/extractFiltersFromParams';
import { getEmptyFilterState } from '@/pages/Catalog/utils/getEmptyFilterState';

import { FilterContext } from './FilterContextBase';

import type { FilterState } from '../../types/filter';

export const FilterProvider: FC<{
  children: ReactNode;
  disableSearchParams?: boolean;
}> = ({ children, disableSearchParams = false }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const isHaveEmptySymbols = useAppSelector(state => state.symbolSelector.symbols.length === 0);
  const location = useLocation();
  const shouldDisableShowSelectedFilter =
    (location.pathname.includes('catalog') || location.pathname.includes('dashboard')) &&
    isHaveEmptySymbols;

  const [localFilters, setLocalFilters] = useState<FilterState>(() =>
    sanitizeShowSelectedFilter(
      disableSearchParams ? getEmptyFilterState() : extractFiltersFromParams(searchParams),
      shouldDisableShowSelectedFilter
    )
  );

  const lastUrlParamsRef = useRef(searchParams.toString());

  const effectiveLocalFilters = useMemo(
    () => sanitizeShowSelectedFilter(localFilters, shouldDisableShowSelectedFilter),
    [localFilters, shouldDisableShowSelectedFilter]
  );

  const localFiltersRef = useRef(effectiveLocalFilters);

  useEffect(() => {
    localFiltersRef.current = effectiveLocalFilters;
  }, [effectiveLocalFilters]);

  const urlFilters = useMemo(
    () =>
      disableSearchParams
        ? null
        : sanitizeShowSelectedFilter(
            extractFiltersFromParams(searchParams),
            shouldDisableShowSelectedFilter
          ),
    [searchParams, disableSearchParams, shouldDisableShowSelectedFilter]
  );

  const syncFromUrl = useEffectEvent((urlFiltersValue: FilterState) => {
    setLocalFilters(urlFiltersValue);
  });

  useEffect(() => {
    if (!disableSearchParams && urlFilters) {
      const currentUrlParams = searchParams.toString();
      const lastUrlParams = lastUrlParamsRef.current;

      if (currentUrlParams !== lastUrlParams) {
        lastUrlParamsRef.current = currentUrlParams;
        if (!areFiltersEqual(localFiltersRef.current, urlFilters)) {
          syncFromUrl(urlFilters);
        }
      }
    }
  }, [urlFilters, searchParams, disableSearchParams]);

  const updateSearchParams = useCallback(
    (updates: Record<string, string | null>) => {
      setSearchParams(params => {
        const currentParams = Object.fromEntries(params.entries());

        for (const [key, value] of Object.entries(updates)) {
          if (value === null || value === '') {
            delete currentParams[key];
          } else {
            currentParams[key] = value;
          }
        }

        // Update ref immediately to track this as an internal update
        const newParams = new URLSearchParams(currentParams);
        lastUrlParamsRef.current = newParams.toString();

        return currentParams;
      });
    },
    [setSearchParams]
  );

  const setFilter = useCallback(
    (key: string, value: string | null | boolean) => {
      const currentValue = localFiltersRef.current[key as keyof FilterState];
      const isToggling =
        key !== 'search' && value !== null && String(value) === String(currentValue);

      const newValue = isToggling ? null : value;
      const nextFiltersForUrl = sanitizeShowSelectedFilter(
        {
          ...localFiltersRef.current,
          [key]: newValue
        },
        shouldDisableShowSelectedFilter
      );
      const nextFilterValue = nextFiltersForUrl[key as keyof FilterState] ?? null;

      setLocalFilters(prev => {
        return sanitizeShowSelectedFilter(
          {
            ...prev,
            [key]: newValue
          },
          shouldDisableShowSelectedFilter
        );
      });

      if (!disableSearchParams) {
        const shouldRemoveShowSelectedParam =
          key === SHOW_SELECTED_URL_PARAM && shouldDisableShowSelectedFilter;
        updateSearchParams({
          [key]:
            shouldRemoveShowSelectedParam || nextFilterValue === null
              ? null
              : String(nextFilterValue)
        });
      }
    },
    [updateSearchParams, disableSearchParams, shouldDisableShowSelectedFilter]
  );

  const clearAllFilters = useCallback(() => {
    setLocalFilters(getEmptyFilterState());

    if (!disableSearchParams) {
      setSearchParams({}, { replace: true });
      lastUrlParamsRef.current = '';
    }
  }, [setSearchParams, disableSearchParams]);

  const isAnyFilterApplied = useMemo(() => {
    const entries = Object.entries(effectiveLocalFilters).filter(
      ([key]) => key !== SHOW_SELECTED_URL_PARAM && key !== 'search'
    );
    return !entries.every(([, value]) => value === null);
  }, [effectiveLocalFilters]);

  const value = useMemo(
    () => ({
      filters: effectiveLocalFilters,
      clearAllFilters,
      setFilter,
      isAnyFilterApplied
    }),
    [effectiveLocalFilters, clearAllFilters, setFilter, isAnyFilterApplied]
  );

  return <FilterContext value={value}>{children}</FilterContext>;
};

const sanitizeShowSelectedFilter = (filters: FilterState, shouldDisableShowSelected: boolean) => {
  if (!shouldDisableShowSelected || !filters[SHOW_SELECTED_URL_PARAM]) return filters;
  return {
    ...filters,
    [SHOW_SELECTED_URL_PARAM]: false
  };
};

const areFiltersEqual = (prevFilters: FilterState, nextFilters: FilterState) => {
  const allKeys = new Set([...Object.keys(prevFilters), ...Object.keys(nextFilters)]);
  return Array.from(allKeys).every(key => {
    const prevValue = String(prevFilters[key as keyof FilterState] ?? null);
    const nextValue = String(nextFilters[key as keyof FilterState] ?? null);
    return prevValue === nextValue;
  });
};
